import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Truck, RotateCcw, HeadphonesIcon, Clock, CreditCard, Eye, Play, ShoppingBag, Award, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { getProducts, getReviews } from '../lib/api';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

// Real product images
const PRODUCT_IMAGES = {
  glove: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/1ezyvaud_IMG_9627.png',
  packaging: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/mrpk2r4p_GHRB.png',
  useCases: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/io9eecdv_C3B90749-3B62-449A-ABCC-0F3E5030098E.png',
  pets: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/7usx8lmo_E84D7770-ABC8-4B32-91DD-87363C019FAB.png',
  storytelling: 'https://customer-assets.emergentagent.com/job_gloovy-store/artifacts/9gnja629_7E6C3F5B-B34B-4EAC-BEA7-5A63035C229E.png',
};

// Stock lifestyle images for storytelling
const LIFESTYLE_IMAGES = {
  dogOnSofa: 'https://images.unsplash.com/photo-1605459082189-dc171c418f08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxkb2clMjBoYWlyJTIwcGV0JTIwZnVyJTIwb24lMjBzb2ZhJTIwY291Y2glMjBtZXNzeXxlbnwwfHx8fDE3NzQyMjU5Nzh8MA&ixlib=rb-4.1.0&q=85',
  cleanSofa: 'https://images.pexels.com/photos/7587783/pexels-photo-7587783.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  heroLivingRoom: 'https://images.pexels.com/photos/6207817/pexels-photo-6207817.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
};

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [stockCount] = useState(Math.floor(Math.random() * 10) + 12);
  const [viewersCount, setViewersCount] = useState(Math.floor(Math.random() * 12) + 9);
  const { addToCart } = useCart();

  // Simulate live viewers
  useEffect(() => {
    const interval = setInterval(() => {
      setViewersCount(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        return Math.max(6, Math.min(22, prev + change));
      });
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, reviewsData] = await Promise.all([
          getProducts(),
          getReviews()
        ]);
        const safeProducts = Array.isArray(productsData) ? productsData : [];
        const safeReviews = Array.isArray(reviewsData) ? reviewsData : [];
        setProducts(safeProducts);
        setReviews(safeReviews);
        
        const gloovy = safeProducts.find(p => p.slug === 'gloovy');
        if (gloovy?.bundles?.length > 0) {
          const popularBundle = gloovy.bundles.find(b => b.popular) || gloovy.bundles[1];
          setSelectedBundle(popularBundle);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const featuredProduct = Array.isArray(products) ? products.find(p => p.slug === 'gloovy') : null;

  const handleAddToCart = (bundle = selectedBundle) => {
    if (featuredProduct) {
      addToCart(featuredProduct, bundle);
      toast.success('Ajouté au panier !', {
        description: bundle?.name || 'Gloovy',
        action: {
          label: 'Voir le panier',
          onClick: () => window.location.href = '/panier'
        }
      });
    }
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  const averageRating = '4.9';

  // UGC testimonials
  const ugcTestimonials = [
    { name: 'Sarah M.', text: 'Franchement j\'y croyais pas mais ça marche trop bien 😳', rating: 5 },
    { name: 'Thomas D.', text: 'Mon canapé est redevenu comme neuf. Avec 2 chats c\'était l\'enfer.', rating: 5 },
    { name: 'Julie L.', text: 'Incroyable ! J\'en ai commandé 3 de plus pour offrir.', rating: 5 },
    { name: 'Marc B.', text: 'Sceptique au début, convaincu en 30 secondes.', rating: 5 },
    { name: 'Claire V.', text: 'Ma voiture est enfin propre. Fini les poils partout !', rating: 5 },
  ];

  // FAQ
  const faqItems = [
    { q: 'Est-ce que ça fonctionne sur tous les tissus ?', a: 'Oui ! Canapé, vêtements, tapis, voiture, literie... Le Gloovy est efficace sur tous les textiles.' },
    { q: 'Est-ce que c\'est lavable ?', a: 'Absolument. Rincez à l\'eau tiède après utilisation ou passez en machine à 30°C.' },
    { q: 'Combien de temps ça dure ?', a: '2-3 ans minimum avec un entretien régulier. C\'est un investissement durable.' },
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden bg-white">
      
      {/* ============ HERO SECTION - LIFESTYLE PREMIUM ============ */}
      <section className="relative overflow-hidden min-h-[620px] lg:min-h-[700px]">
        {/* Living room background — blurred for depth of field */}
        <div className="absolute inset-0">
          <img
            src={LIFESTYLE_IMAGES.heroLivingRoom}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: 'blur(4px) saturate(0.85) brightness(1.02)' }}
          />
        </div>
        
        {/* Warm overlay for brand cohesion */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/75 to-white/40 pointer-events-none" />
        <div className="absolute inset-0 bg-[#f5f0eb]/30 pointer-events-none" />
        {/* Bottom fade for smooth section transition */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none z-[2]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            
            {/* Left - Text Content */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="text-center lg:text-left order-2 lg:order-1 relative z-10"
            >
              {/* Badges */}
              <motion.div variants={fadeIn} className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-7">
                <span className="inline-flex items-center gap-1.5 bg-orange-500/10 text-orange-600 text-sm font-medium px-3.5 py-1.5 rounded-full border border-orange-500/20">
                  <Clock className="w-3.5 h-3.5" />
                  Plus que {stockCount} en stock
                </span>
                <span className="inline-flex items-center gap-1.5 bg-gray-900/[0.06] text-gray-600 text-sm font-medium px-3.5 py-1.5 rounded-full">
                  <ShoppingBag className="w-3.5 h-3.5" />
                  +1000 vendus ce mois
                </span>
              </motion.div>
              
              {/* Headline — primary focal point */}
              <motion.h1 
                variants={fadeIn}
                className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-[1.08] tracking-tight"
                data-testid="hero-headline"
              >
                Votre canapé plein de poils ?
                <br />
                <span className="text-orange-500">Nettoyez-le en 10 secondes.</span>
              </motion.h1>
              
              <motion.p 
                variants={fadeIn}
                className="font-jakarta text-base lg:text-lg text-gray-500 mb-7 max-w-md mx-auto lg:mx-0 leading-relaxed"
              >
                Le gant Gloovy élimine instantanément les poils d'animaux, sans effort. Double action silicone + électrostatique.
              </motion.p>

              {/* Rating */}
              <motion.div variants={fadeIn} className="flex items-center justify-center lg:justify-start gap-3 mb-9">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="font-jakarta text-gray-900 font-medium">{averageRating}/5</span>
                <span className="font-jakarta text-gray-400">({(reviews?.length || 0) + 847} avis)</span>
              </motion.div>
              
              {/* CTA */}
              <motion.div variants={fadeIn} className="space-y-5">
                <Link to="/produits/gloovy">
                  <Button 
                    size="lg"
                    className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white font-jakarta font-bold px-10 py-7 rounded-full text-lg shadow-[0_8px_30px_rgba(0,0,0,0.15)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.2)] transition-all duration-300"
                    data-testid="hero-cta-buy"
                  >
                    Acheter maintenant
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                
                {/* Trust line */}
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-5 text-gray-400 font-jakarta text-sm pt-1">
                  <span className="flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-green-500" /> Livraison rapide
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-green-500" /> Garantie 30 jours
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-green-500" /> Paiement sécurisé
                  </span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right - Product (packaging + gloves) in lifestyle context */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="relative order-1 lg:order-2 flex items-center justify-center"
            >
              {/* BEST SELLER Badge */}
              <div className="absolute -top-1 right-2 lg:right-0 z-20">
                <span className="inline-flex items-center gap-1.5 bg-orange-500 text-white font-outfit font-bold text-sm px-4 py-2 rounded-full shadow-[0_4px_15px_rgba(249,115,22,0.3)]">
                  <Award className="w-4 h-4" />
                  N°1 DES VENTES
                </span>
              </div>

              {/* Live viewers */}
              <div className="absolute top-0 left-2 lg:left-0 z-20 bg-white/80 backdrop-blur-md rounded-full px-3 py-1.5 flex items-center gap-2 shadow-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span className="font-jakarta text-xs text-gray-600">
                  {viewersCount} regardent
                </span>
              </div>

              {/* Product container — packaging with soft natural shadow */}
              <div className="relative w-full max-w-lg mx-auto pt-6">
                {/* Soft ground shadow */}
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[75%] h-8 bg-black/[0.07] rounded-full blur-2xl pointer-events-none" />
                
                <img
                  src={PRODUCT_IMAGES.packaging}
                  alt="Gloovy - Kit complet anti-poils"
                  className="relative w-full h-auto"
                  style={{
                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.12)) drop-shadow(0 8px 16px rgba(0,0,0,0.08))',
                  }}
                  data-testid="hero-product-image"
                />
              </div>

              {/* Floating trust badge */}
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-white rounded-full px-5 py-2.5 shadow-[0_4px_20px_rgba(0,0,0,0.08)] flex items-center gap-2 z-20 border border-gray-100">
                <Shield className="w-5 h-5 text-green-500" />
                <span className="font-jakarta text-sm font-semibold text-gray-900">Produit testé & validé</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ VIDEO SECTION ============ */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Regardez, ça marche vraiment.
            </h2>
          </div>
          
          {/* Video Container */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="relative aspect-video rounded-2xl overflow-hidden bg-gray-900 cursor-pointer group"
            data-testid="video-demo"
          >
            <img
              src={PRODUCT_IMAGES.useCases}
              alt="Démonstration Gloovy"
              className="w-full h-full object-cover"
            />
            
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                <Play className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" />
              </div>
            </div>
            
            <div className="absolute bottom-6 left-6">
              <p className="font-outfit text-3xl font-bold text-white drop-shadow-lg">
                1 passage. <span className="text-orange-400">0 poil.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ STORYTELLING: 3-STEP VISUAL ============ */}
      <section className="py-16 lg:py-24 bg-[#0a0a0a]" data-testid="storytelling-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <span className="inline-block bg-orange-500/15 text-orange-400 font-jakarta text-sm font-medium px-4 py-1.5 rounded-full border border-orange-500/20 mb-4">
              Avant / Après
            </span>
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Voyez la différence.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Step 1 — PROBLEM */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="relative group"
              data-testid="storytelling-step-1"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden relative">
                <img
                  src={LIFESTYLE_IMAGES.dogOnSofa}
                  alt="Chien sur canapé avec des poils"
                  className="w-full h-full object-cover"
                  style={{ filter: 'saturate(0.8) contrast(1.05)' }}
                />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                {/* Step number */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white font-outfit font-bold text-sm">
                    1
                  </span>
                </div>
                {/* Text */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="font-outfit text-2xl lg:text-3xl font-bold text-white leading-tight">
                    Des poils<br />partout ?
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Step 2 — ACTION */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="relative group"
              data-testid="storytelling-step-2"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden relative bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d]">
                {/* Glow behind glove */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-orange-500/[0.1] rounded-full blur-[60px]" />
                <img
                  src={PRODUCT_IMAGES.glove}
                  alt="Gant Gloovy en action"
                  className="w-full h-full object-contain p-6 relative z-10"
                  style={{ filter: 'contrast(1.1) brightness(1.05)' }}
                />
                {/* Step number */}
                <div className="absolute top-4 left-4 z-20">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-500/20 backdrop-blur-sm border border-orange-500/30 text-orange-400 font-outfit font-bold text-sm">
                    2
                  </span>
                </div>
                {/* Text */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                  <p className="font-outfit text-2xl lg:text-3xl font-bold text-white leading-tight">
                    1 passage.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Step 3 — RESULT */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="relative group"
              data-testid="storytelling-step-3"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden relative">
                <img
                  src={LIFESTYLE_IMAGES.cleanSofa}
                  alt="Canapé propre sans poils"
                  className="w-full h-full object-cover"
                  style={{ filter: 'saturate(0.85) contrast(1.05) brightness(1.05)' }}
                />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                {/* Step number */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-400 font-outfit font-bold text-sm">
                    3
                  </span>
                </div>
                {/* Text */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="font-outfit text-2xl lg:text-3xl font-bold text-white leading-tight">
                    0 poil.
                  </p>
                  <span className="inline-flex items-center gap-1.5 bg-green-500/20 text-green-400 text-sm font-jakarta font-medium px-3 py-1 rounded-full mt-3 border border-green-500/25">
                    <Check className="w-3.5 h-3.5" />
                    Résultat immédiat
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ USE CASES ============ */}
      <section className="bg-white">
        <img 
          src={PRODUCT_IMAGES.useCases}
          alt="Nettoie vêtements, canapés, tapis"
          className="w-full h-auto"
          data-testid="use-cases-image"
        />
      </section>

      {/* ============ PRODUCT CLOSE-UP ============ */}
      <section className="py-12 lg:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="bg-gray-900 rounded-2xl overflow-hidden">
              <img
                src={PRODUCT_IMAGES.glove}
                alt="Gant Gloovy"
                className="w-full h-auto"
              />
            </div>
            <div>
              <span className="inline-block bg-orange-100 text-orange-600 font-jakarta text-sm font-semibold px-3 py-1 rounded-full mb-4">
                Double action brevetée
              </span>
              <h3 className="font-outfit text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                Pourquoi ça marche
              </h3>
              <ul className="space-y-4">
                {[
                  'Picots silicone qui attrapent les poils',
                  'Tissu électrostatique qui les retient',
                  'Efficace en 1 seul passage',
                  'Lavable et réutilisable à vie',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="font-jakarta text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PETS ============ */}
      <section className="bg-white">
        <img 
          src={PRODUCT_IMAGES.pets}
          alt="Convient aux chiens et chats"
          className="w-full h-auto"
          data-testid="pets-image"
        />
      </section>

      {/* ============ OFFERS ============ */}
      {featuredProduct?.bundles?.length > 0 && (
        <section className="py-12 lg:py-20 bg-gray-900">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Live indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <Eye className="w-4 h-4 text-gray-400" />
              <span className="font-jakarta text-sm text-gray-400">
                {viewersCount} personnes regardent ce produit
              </span>
            </div>
            
            <div className="text-center mb-10">
              <span className="inline-block bg-orange-500/20 text-orange-400 font-jakarta text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                Offre limitée
              </span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-2">
                Choisissez votre offre
              </h2>
              <p className="font-jakarta text-gray-400">Plus vous achetez, plus vous économisez</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              {featuredProduct.bundles?.map((bundle, index) => (
                <motion.div 
                  key={bundle.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedBundle(bundle)}
                  className={`relative cursor-pointer rounded-2xl transition-all ${
                    bundle.popular 
                      ? 'bg-gradient-to-b from-orange-500 to-orange-600 p-[2px] md:scale-105 z-10' 
                      : 'bg-white/10 border border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`home-bundle-${bundle.id}`}
                >
                  <div className={`h-full rounded-2xl p-6 ${bundle.popular ? 'bg-gray-900' : ''}`}>
                    {bundle.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-orange-500 text-white font-outfit font-bold text-sm px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                          MEILLEURE OFFRE 🔥
                        </span>
                      </div>
                    )}
                    {index === 2 && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-green-500 text-white font-outfit font-bold text-sm px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                          Économie maximale
                        </span>
                      </div>
                    )}
                    
                    <div className="text-center pt-2">
                      <h3 className="font-outfit text-xl font-bold text-white mb-1">
                        {bundle.name}
                      </h3>
                      
                      <div className="my-4">
                        <span className="font-outfit text-4xl lg:text-5xl font-bold text-white">
                          {bundle.price.toFixed(2)}€
                        </span>
                        {bundle.original_price && (
                          <span className="block font-jakarta text-sm line-through text-white/40 mt-1">
                            {bundle.original_price.toFixed(2)}€
                          </span>
                        )}
                      </div>
                      
                      <span className={`inline-block font-jakarta text-sm font-semibold px-3 py-1 rounded-full mb-5 ${
                        bundle.popular ? 'bg-green-500 text-white' : 'bg-green-500/20 text-green-400'
                      }`}>
                        -{bundle.savings}
                      </span>
                      
                      <Button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBundle(bundle);
                          handleAddToCart(bundle);
                        }}
                        className={`w-full py-5 rounded-full font-jakarta font-semibold ${
                          bundle.popular 
                            ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                            : 'bg-white hover:bg-gray-100 text-gray-900'
                        }`}
                        data-testid={`add-bundle-${bundle.id}`}
                      >
                        Ajouter au panier
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Trust */}
            <div className="flex flex-wrap justify-center gap-6 mt-10 text-gray-400 font-jakarta text-sm">
              <span className="flex items-center gap-1.5">
                <CreditCard className="w-4 h-4" /> Paiement sécurisé
              </span>
              <span className="flex items-center gap-1.5">
                <Truck className="w-4 h-4" /> Livraison rapide
              </span>
              <span className="flex items-center gap-1.5">
                <RotateCcw className="w-4 h-4" /> Garantie 30 jours
              </span>
              <span className="flex items-center gap-1.5">
                <HeadphonesIcon className="w-4 h-4" /> Support client
              </span>
            </div>
          </div>
        </section>
      )}

      {/* ============ UGC TESTIMONIALS ============ */}
      <section className="py-12 lg:py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Ce qu'ils en disent
            </h2>
            <div className="flex items-center justify-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span className="font-jakarta text-gray-600">{averageRating}/5 • {(reviews?.length || 0) + 847} avis</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {ugcTestimonials.map((t, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm"
                data-testid={`ugc-card-${i}`}
              >
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="font-jakarta text-gray-700 text-sm mb-4 leading-relaxed">
                  "{t.text}"
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-jakarta text-gray-900 text-sm font-semibold">{t.name}</span>
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Vérifié
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/avis">
              <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full">
                Voir tous les avis
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ MINI FAQ ============ */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-outfit text-2xl font-bold text-gray-900 mb-8 text-center">
            Questions fréquentes
          </h2>

          <Accordion type="single" collapsible className="space-y-3">
            {faqItems.map((item, i) => (
              <AccordionItem 
                key={i} 
                value={`faq-${i}`}
                className="border border-gray-200 rounded-xl px-5 bg-gray-50"
                data-testid={`home-faq-${i}`}
              >
                <AccordionTrigger className="font-jakarta font-semibold text-gray-900 hover:no-underline py-4 text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="font-jakarta text-gray-600 pb-4">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============ TRUST BADGES ============ */}
      <section className="py-8 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: CreditCard, title: 'Paiement sécurisé' },
              { icon: Truck, title: 'Livraison 48h' },
              { icon: RotateCcw, title: 'Satisfait ou remboursé' },
              { icon: HeadphonesIcon, title: 'Support réactif' }
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 text-gray-600">
                <item.icon className="w-5 h-5 text-orange-500" />
                <span className="font-jakarta text-sm font-medium">{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-12 lg:py-16 bg-orange-500">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-4">
            Prêt à dire adieu aux poils ?
          </h2>
          <p className="font-jakarta text-white/80 mb-8">
            Rejoignez +1000 clients satisfaits. Satisfait ou remboursé 30 jours.
          </p>
          <Link to="/produits/gloovy">
            <Button 
              size="lg"
              className="bg-white text-orange-600 hover:bg-gray-100 font-jakarta font-bold px-10 py-7 rounded-full text-lg"
              data-testid="final-cta-btn"
            >
              Commander maintenant
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ============ MOBILE STICKY CTA ============ */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white border-t border-gray-200 p-3 shadow-lg">
        <div className="flex items-center gap-2 mb-2 justify-center">
          <Eye className="w-3.5 h-3.5 text-gray-400" />
          <span className="font-jakarta text-xs text-gray-500">{viewersCount} personnes regardent</span>
        </div>
        <Button 
          onClick={() => handleAddToCart()}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-jakarta font-bold py-4 rounded-full"
          data-testid="mobile-sticky-cta"
        >
          Acheter maintenant – {selectedBundle?.price.toFixed(2) || '19.90'}€
        </Button>
      </div>
      
      <div className="h-24 lg:hidden" />
    </div>
  );
};

export default HomePage;
